<template>
  <div id="staffRequirementSubmissionForm" class="form">
    <v-subheader class="d-flex flex-column">
      <div v-if="staff">
        Staff Name: {{ staff.firstName }} {{ staff.lastName }}
      </div>
    </v-subheader>

    <v-form v-if="loaded" ref="form" v-model="valid" lazy-validation>
      <div class="mx-md-4">
        <v-select
          v-model="staffRequirementSubmission.submissionMethod"
          :items="submissionMethods"
          label="Submission Method"
          outlined
        ></v-select>

        <v-textarea
          label="Notes"
          :auto-grow="true"
          :rows="3"
          outlined
          placeholder="Optional"
          v-model="staffRequirementSubmission.notes"
        />
      </div>

      <div
        v-if="
          staffRequirementSubmission.requirements &&
          staffRequirementSubmission.requirements.length > 0
        "
      >
        <v-subheader>Requirements</v-subheader>
        <v-row
          class="ml-4 my-2"
          no-gutters
          v-for="(req, index) in staffRequirementSubmission.requirements"
          :key="req.id"
        >
          <v-col
            :cols="req.type == 'Vaccination' ? 4 : 12"
            md="4"
            class="my-auto"
          >
            <div>
              {{ index + 1 }}.
              <span class="mx-md-4">
                {{ req.name }}
                <div
                  v-if="req.attachmentRequired"
                  class="text-caption ml-4 ml-md-8"
                >
                  Require proof
                </div>
              </span>
            </div>
          </v-col>
          <v-col class="my-auto">
            <span v-if="req.type == 'Vaccination' && req.forDoseNumber">
              <date-picker
                :rules="[rules.required]"
                v-model="req.vaccinationDate"
                outlined
                dense
                :label="`Dose ${req.forDoseNumber} date`"
              />
            </span>
            <span
              class="d-flex flex-column flex-md-row my-2 my-md-0"
              v-if="req.type == 'Certification'"
            >
              <date-picker
                :rules="[rules.required]"
                label="Issue Date"
                :show-icon="false"
                v-model="req.cerfIssueDate"
                outlined
                dense
                hide-details
                @change="updateCertificateExpiration"
                class="mr-2"
              />

              <span class="mx-2 my-auto nowrap"
                >Expires on {{ req.cerfExpireDate | formatDateYear }}</span
              >
            </span>
          </v-col>
          <v-col
            v-if="staffRequirementSubmission.requirements.length > 1"
            class="text-right mx-2"
          >
            <v-btn @click="deleteRequirement(req)" icon
              ><v-icon>mdi-delete-outline</v-icon></v-btn
            >
          </v-col>
        </v-row>
      </div>
      <v-divider class="my-5" />

      <div
        class="mb-5 flex-grow-1"
        v-if="
          staffRequirementSubmission.attachments &&
          staffRequirementSubmission.attachments.length > 0
        "
      >
        <attachments-view v-model="staffRequirementSubmission.attachments" />
      </div>

      <div class="mx-md-4">
        <image-upload maxSize="52428800" v-model="files" />
      </div>

      <v-divider class="my-8" />
      <div class="mt-4 d-flex justify-space-between">
        <v-btn @click="goBack"> Back </v-btn>
        <v-btn
          :loading="isAppLoading"
          :disabled="isAppLoading"
          @click="handleSubmit"
          color="primary"
        >
          {{ id ? "Update" : "Save" }}
        </v-btn>
        <span></span>
      </div>
    </v-form>

    <div v-if="!hasDependencies">staff or requirement not found</div>
  </div>
</template>

<script>
import { getStaffRequirementSubmission } from "@/services/staffRequirementSubmission";
import moment from "moment";
import { getStaff } from "@/services/staff";

import { getRequirements } from "@/services/requirement";
import { rules } from "@/store/util/rules.js";

import { mapActions, mapGetters } from "vuex";
const AttachmentsView = () => import("../common/AttachmentsView.vue");

export default {
  props: ["id", "staffId", "requirements"],
  data() {
    return {
      loaded: false,
      valid: false,
      staff: null,
      requirementIds: [],
      submissionMethods: ["Email", "In-person", "Online"],
      requirement2Dose: null,
      requirement2CerfDates: null,
      staffRequirementSubmission: {
        staffId: null,
        requirements: [
          {
            requirementId: null,
            forDoseNumber: null,
            vaccinationDate: null,
            cerfIssueDate: null,
            cerfExpireDate: null,
          },
        ],
        submissionMethod: "In-person",
        state: "",
        notes: "",
        attachments: [],
      },
      files: [],
      rules,
    };
  },
  computed: {
    ...mapGetters("navigation", ["isAppLoading"]),

    hasDependencies() {
      return (
        this.staffRequirementSubmission.requirements &&
        this.staffRequirementSubmission.requirements.length > 0 &&
        this.staff
      );
    },
    proofRequired() {
      if (
        this.staffRequirementSubmission.requirements &&
        this.staffRequirementSubmission.requirements.length > 0
      ) {
        let requireProof = false;
        for (const requirement of this.staffRequirementSubmission
          .requirements) {
          requireProof = requireProof || requirement.attachmentRequired;
        }
        return requireProof;
      } else {
        return false;
      }
    },
  },
  created() {
    if (!this.id) {
      this.initNew();
    }

    this.setInstance();
  },
  methods: {
    ...mapActions("staffRequirementSubmission", [
      "createStaffRequirementSubmission",
      "updateStaffRequirementSubmission",
    ]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    goBack() {
      this.$router.go(-1);
    },
    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        this.addErrorMessage("Please check your inputs");
        return;
      }

      let staffRequirementSubmission = Object.assign(
        {},
        this.staffRequirementSubmission
      );

      if (this.id) {
        delete staffRequirementSubmission.relations;

        this.updateStaffRequirementSubmission(staffRequirementSubmission)
          .then(async () => {
            if (this.files) {
              this.addMessage("Uploading attachment, please wait...");
              await this.uploadFiles(this.id, this.files)
                .then(() => {
                  this.addSuccessMessage("Submission was updated successfully");
                  this.backTostaffView();
                })
                .catch(() => {
                  this.addErrorMessage("Error uploading attachments");
                });
            } else {
              this.addSuccessMessage("Submission was updated successfully");
            }
          })
          .catch(() => {
            this.addErrorMessage(
              "Error updating submission, please try again later"
            );
          });
      } else {
        if (this.proofRequired && this.files.length < 1) {
          this.addErrorMessage("Proof is required, please add an attachment");
          return;
        }

        this.createStaffRequirementSubmission(staffRequirementSubmission)
          .then(async (response) => {
            const submissionId = response;
            if (this.files) {
              this.addMessage("Uploading attachment, please wait...");
              await this.uploadFiles(submissionId, this.files)
                .then(() => {
                  this.addSuccessMessage("Submission was updated successfully");
                  this.backTostaffView();
                })
                .catch(() => {
                  this.addErrorMessage("Error uploading attachments");
                });
            } else {
              this.addSuccessMessage("Submission was updated successfully");
            }
          })
          .catch(() => {
            this.addErrorMessage(
              "Error submitting submission, please try again later"
            );
          });
      }
    },

    deleteRequirement(requirement) {
      const index = this.staffRequirementSubmission.requirements.findIndex(
        (r) =>{
          let match = r.requirementId == requirement.requirementId;
          if(requirement.type == 'Vaccination'){
            match = match && requirement.forDoseNumber == r.forDoseNumber;
          }
          return match;
        }
      );
      this.staffRequirementSubmission.requirements.splice(index, 1);
    },
    initNew() {
      const ids = new Set();
      this.requirement2Dose = new Map();
      if (this.requirements) {
        String(this.requirements)
          .split(",")
          .map((r) => {
            const requireIdDose = r.split("_");
            const id = parseInt(requireIdDose[0]);
            const dose = requireIdDose.length == 2 ? requireIdDose[1] : null;
            ids.add(id);
            this.requirementIds = Array.from(ids);

            if (dose) {
              const doseInfo = { dose, vaccinationDate: null };
              if (this.requirement2Dose.get(id)) {
                this.requirement2Dose.get(id).push(doseInfo);
              } else {
                this.requirement2Dose.set(id, [doseInfo]);
              }
            }
          });
      }
      this.staffRequirementSubmission.staffId = this.staffId;
    },
    async uploadFiles(submissionId, files) {
      if (files && files.length > 0) {
        var formData = new FormData();
        formData.append("staffId", this.staff.id);
        for (const f of files) {
          formData.append("files", f, f.name);
        }

        this.addMessage("Uploading images...");
        return await this.$axios
          .post(
            `/staffRequirementSubmission/upload/${submissionId}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          )
          .then((resp) => {
            const savedFiles = resp.data;
            const uploadCount = savedFiles.files ? savedFiles.files.length : "";
            this.addSuccessMessage(
              `Uploaded ${uploadCount} files successfully`
            );
          });
      }
    },
    viewSubmit(submissionId) {
      this.$router.push({
        name: "staffRequirementSubmissionView",
        params: { id: submissionId },
      });
    },
    backTostaffView() {
      const id = this.staffId ? this.staffId : this.staff.id;
      this.$router.push({ name: "staffView", params: { id } });
    },
    setInstance() {
      if (this.id) {
        this.staffRequirementSubmission.id = this.id;
        getStaffRequirementSubmission(this.id).then((response) => {
          let instance = response.data;
          for (let property in instance) {
            if (
              Object.prototype.hasOwnProperty.call(instance, property) &&
              Object.prototype.hasOwnProperty.call(
                this.staffRequirementSubmission,
                property
              )
            ) {
              this.staffRequirementSubmission[property] = instance[property];
            }
          }
          this.prepInstance();
          this.setDependencies();
        });
      } else {
        this.setDependencies();
      }
    },
    prepInstance() {
      //set requirementIds
      this.requirementIds = [];
      this.requirement2Dose = new Map();
      this.requirement2CerfDates = new Map();
      this.staffRequirementSubmission.requirements.forEach((r) => {
        const id = r.requirementId;
        this.requirementIds.push(id);
        const dose = r.forDoseNumber;
        if (dose) {
          const doseInfo = { dose, vaccinationDate: r.vaccinationDate };
          if (this.requirement2Dose.get(id)) {
            this.requirement2Dose.get(id).push(doseInfo);
          } else {
            this.requirement2Dose.set(id, [doseInfo]);
          }
        }
        this.requirement2CerfDates.set(id, [r.cerfIssueDate, r.cerfExpireDate]);
      });

      if (this.staffRequirementSubmission.attachments) {
        this.staffRequirementSubmission.attachments.forEach((file) => {
          const path = `${process.env.VUE_APP_BASE_URL}/compliance/submission/attachment/${file.id}`;
          file.path = path;
        });
      }
    },
    async setDependencies() {
      if (this.staffRequirementSubmission.staffId) {
        await getStaff(this.staffRequirementSubmission.staffId).then(
          (response) => {
            this.staff = response.data;
          }
        );
      }
      if (this.requirementIds && this.requirementIds.length > 0) {
        const ids = this.requirementIds.join(",");
        await getRequirements(ids)
          .then((response) => {
            const requirements = response.data;
            const submitRequirements = [];
            requirements.forEach((r) => {
              if (
                r.type == "Vaccination" &&
                this.requirement2Dose &&
                this.requirement2Dose.get(r.id)
              ) {
                const doses = this.requirement2Dose.get(r.id);
                doses.forEach((doseInfo) => {
                  const req = {
                    type: r.type,
                    name: r.name,
                    requirementId: r.id,
                    forDoseNumber: doseInfo.dose,
                    vaccinationDate: doseInfo.vaccinationDate,
                    attachmentRequired: r.attachmentRequired,
                    originalRequired: r.originalRequired,
                  };
                  submitRequirements.push(req);
                });
              } else {
                const req = {
                  type: r.type,
                  name: r.name,
                  requirementId: r.id,
                  forDoseNumber: r.forDoseNumber,
                  vaccinationDate: r.vaccinationDate,
                  cerfIssueDate: this.requirement2CerfDates
                    ? this.requirement2CerfDates.get(r.id)[0]
                    : null,
                  cerfExpireDate: this.requirement2CerfDates
                    ? this.requirement2CerfDates.get(r.id)[1]
                    : null,
                  attachmentRequired: r.attachmentRequired,
                  originalRequired: r.originalRequired,
                };

                if (r.ruleCertification) {
                  req.validationPeriod = r.ruleCertification.validationPeriod;
                  req.validationPeriodUnit =
                    r.ruleCertification.validationPeriodUnit;
                  if (!req.cerfIssueDate) {
                    req.cerfIssueDate = moment().format("YYYY-MM-DD");
                  }
                  req.cerfExpireDate = moment(req.cerfIssueDate)
                    .add(req.validationPeriod, req.validationPeriodUnit)
                    .format("YYYY-MM-DD");
                }
                submitRequirements.push(req);
              }
            });

            this.staffRequirementSubmission.requirements = submitRequirements;
          })
          .catch((error) => {
            this.addErrorMessage(error.message);
          });
      }

      this.loaded = true;
    },
    updateCertificateExpiration() {
      if (
        this.staffRequirementSubmission &&
        this.staffRequirementSubmission.requirements &&
        this.staffRequirementSubmission.requirements.length > 0
      ) {
        this.staffRequirementSubmission.requirements.forEach((r) => {
          if (
            r.type == "Certification" &&
            r.cerfIssueDate &&
            r.validationPeriod &&
            r.validationPeriodUnit
          ) {
            r.cerfExpireDate = moment(r.cerfIssueDate)
              .add(r.validationPeriod, r.validationPeriodUnit)
              .format("YYYY-MM-DD");
          }
        });
      }
    },
  },
  components: {
    AttachmentsView,
  },
};
</script>
<style scoped>
.nowrap {
  white-space: nowrap;
}
</style>
